import React from 'react';
import { Box, Typography, IconButton, Chip, Tooltip } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { StatusChip } from '../ui';
import { formatCurrency } from '../../utils/currencyFormatter';

// Days from today to the due date: negative = overdue. Only unpaid (issued)
// invoices get due-date urgency treatment.
export const dueInDays = (row) => {
  if (!row.dueDate || row.status !== 'issued') return null;
  const startOfToday = new Date();
  startOfToday.setHours(0, 0, 0, 0);
  const due = new Date(row.dueDate);
  due.setHours(0, 0, 0, 0);
  return Math.round((due - startOfToday) / 86400000);
};

export const DUE_SOON_DAYS = 7;

const buildMyInvoicesColumns = ({ t, i18n, onDownload }) => [
  {
    field: 'invoiceNumber',
    headerName: t('Invoice'),
    flex: 1.6,
    minWidth: 180,
    renderCell: (params) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" noWrap>{params.row.invoiceNumber}</Typography>
        {/* single secondary line — a third line gets clipped by the fixed row height */}
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.row.createdAt ? new Date(params.row.createdAt).toLocaleDateString() : ''}
          {(params.row.revision || 0) > 0 && params.row.revisedAt && (
            <Box component="span" sx={{ color: 'warning.main' }}>
              {' · '}
              {t('Revised {{date}}', {
                date: new Date(params.row.revisedAt).toLocaleDateString(),
                defaultValue: `Revised ${new Date(params.row.revisedAt).toLocaleDateString()}`,
              })}
            </Box>
          )}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'dueDate',
    headerName: t('Due', { defaultValue: 'Due' }),
    flex: 0.9,
    minWidth: 120,
    renderCell: (params) => {
      if (!params.row.dueDate) return <Typography variant="body2" color="text.secondary">—</Typography>;
      const days = dueInDays(params.row);
      const overdue = days !== null && days < 0;
      const dueSoon = days !== null && days >= 0 && days <= DUE_SOON_DAYS;
      return (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ color: overdue ? 'error.main' : dueSoon ? 'warning.main' : 'text.primary' }} noWrap>
            {new Date(params.row.dueDate).toLocaleDateString()}
          </Typography>
          {overdue && (
            <Typography variant="body2" color="error.main" noWrap>
              {t('Overdue', { defaultValue: 'Overdue' })}
            </Typography>
          )}
          {dueSoon && (
            <Typography variant="body2" color="warning.main" noWrap>
              {days === 0
                ? t('Due today', { defaultValue: 'Due today' })
                : t('Due in {{count}} days', { count: days, defaultValue: `Due in ${days} days` })}
            </Typography>
          )}
        </Box>
      );
    },
  },
  {
    field: 'status',
    headerName: t('Status'),
    flex: 0.9,
    minWidth: 120,
    renderCell: (params) => (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
        <StatusChip status={params.value} label={t(params.value.charAt(0).toUpperCase() + params.value.slice(1))} />
        {(params.row.revision || 0) > 0 && (
          <Tooltip title={t('This invoice was updated after being issued. Download the PDF for the latest version.', { defaultValue: 'This invoice was updated after being issued. Download the PDF for the latest version.' })}>
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              label={t('Revised', { defaultValue: 'Revised' })}
            />
          </Tooltip>
        )}
      </Box>
    ),
  },
  {
    field: 'total',
    headerName: t('Total'),
    flex: 0.8,
    minWidth: 100,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params) => <Typography variant="subtitle1">{formatCurrency(params.value, params.row.currency, i18n.language)}</Typography>,
  },
  {
    field: 'actions',
    headerName: '',
    width: 72,
    sortable: false,
    filterable: false,
    renderCell: (params) => (
      <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDownload(params.row); }} aria-label={t('Download PDF')}>
        <DownloadIcon fontSize="small" />
      </IconButton>
    ),
  },
];

export default buildMyInvoicesColumns;
